import { useRouter } from 'next/router';
import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { mqFrom } from 'styles/styles';
import i18n, { SettingsType } from 'utils/i18n';
import Button from './button';

type AssessmentQuizFormProps = {
  quiz: QuizItem[] | undefined;
  submitting: boolean;
  selectedAnswers: Record<string, string>;
  onComplete: (selectedData: Record<string, string>) => void;
  goBack: () => void;
  stepNum: number;
  totalSteps: number;
};

const AssessmentQuizForm: React.FC<AssessmentQuizFormProps> = ({
  quiz = [],
  onComplete,
  selectedAnswers,
  submitting,
  stepNum,
  totalSteps,
  goBack,
}) => {
  const [selected, setSelected] = useState<Record<string, string>>(selectedAnswers || {});
  const [validationText, setValidationText] = useState('');
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;

  const selectAnswer = (questionId: string, answerId: string): void => {
    setSelected({ ...selected, [questionId]: answerId });
  };

  useEffect(() => {
    setSelected(selectedAnswers || {});
  }, [JSON.stringify(selectedAnswers || {})]); // eslint-disable-line

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    // Check if all questions are answered

    let hasUnselected = false;
    quiz.forEach((q) => {
      const selectedAnswer = selected[q.id];
      if (!selectedAnswer) {
        hasUnselected = true;
      }
    });

    // If has unslected answers, show validation text
    if (hasUnselected) {
      setValidationText(dictionary.quizValidationAllFieldsAreRequired);
    } else {
      setValidationText('');
      onComplete(selected);
    }
  };

  if (!quiz.length) return null;

  return (
    <StyledAssessmentQuizForm>
      <form onSubmit={onSubmit}>
        <div className="questions">
          {quiz.map((question, index) => {
            return (
              <div key={question.id} className="question">
                <div className="label">
                  <span>{index + 1}.</span> {question.label}
                </div>
                <div className="answers">
                  {question.answers.map((answer) => {
                    const isSelected = selected[question.id] === answer.id;
                    return (
                      <Fragment key={answer.id}>
                        <div
                          role="radio"
                          aria-checked={isSelected ? 'true' : 'false'}
                          className={`answer ${isSelected ? 'selected' : ''}`}
                          onClick={() => selectAnswer(question.id, answer.id)}
                          onKeyUp={() => selectAnswer(question.id, answer.id)}
                          tabIndex={0}
                        >
                          <div className="radio"></div>
                          <div className="label">{answer.label}</div>
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="buttons">
          {stepNum > 1 && (
            <Button
              mode="secondary"
              onClick={() => {
                goBack();
              }}
            >
              {dictionary.assessmentBack}
            </Button>
          )}
          <Button mode="primary" htmlType="submit" loading={submitting}>
            {dictionary.quizCheckAnswers}
          </Button>
          {totalSteps > 1 && (
            <div>
              {stepNum} of {totalSteps}
            </div>
          )}
        </div>
        {validationText && <div className="validation-text">{validationText}</div>}
      </form>
    </StyledAssessmentQuizForm>
  );
};

export const StyledAssessmentQuizForm = styled.ul`
  margin: 1rem 0;

  .question {
    margin: 1.5rem 0 1rem 0;

    > .label {
      span {
        font-weight: 600;
      }
    }
  }

  .answers {
    margin: 0.5rem 0;
    ${mqFrom.tablet} {
      margin: 0.5rem 3rem 0 3rem;
    }
  }

  .answer {
    padding: 0.5rem 1rem;

    margin-bottom: 0.5rem;
    position: relative;
    display: flex;
    align-items: center;
    background-color: var(--body-background-medium);
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;

    ${mqFrom.tablet} {
      background-color: transparent;
      border-radius: 1rem;
      padding: 0 1rem;
    }

    &:hover {
      background-color: transparent;
      border-color: transparent;
      box-shadow: 0 0 5px 0 var(--box-shadow-light);
    }

    .radio {
      width: 14px;
      height: 14px;
      border: 1px solid var(--text);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: 1rem;
    }

    > .label {
      flex: 1 1 0;
    }

    &.selected {
      background-color: var(--primary-background);
      border-color: var(--primary-base);

      .radio {
        border: 1px solid var(--primary-base);

        &:before {
          content: '';
          /* display: block; */
          width: 6px;
          height: 6px;
          background: var(--primary-base);
          border-radius: 50%;
        }
      }
    }
  }

  .buttons {
    margin: 1rem 0;
  }

  .validation-text {
    margin-top: 0.5rem;
    color: var(--danger-base);
    font-weight: 600;
    font-size: 0.875rem;
  }
`;

export default AssessmentQuizForm;
